import type { Question } from '@trivia/shared';

/** People, populations and the shape of humanity. */
export const humanQuestions: Question[] = [
  {
    id: 'humans.northern-hemisphere',
    type: 'percentage',
    prompt: 'What percentage of the world’s population lives in the Northern Hemisphere?',
    category: 'People',
    difficulty: 2,
    tags: ['geography', 'population'],
    answer: 87,
    displayAnswer: 'about 87%',
    tolerance: 12,
    reveal: {
      headline: 'about 87%',
      explanation:
        'Nearly all of Asia, Europe, North America and most of Africa sit north of the equator. The Southern Hemisphere holds most of the ocean and only a handful of large populations.',
      comparison: 'Roughly nine out of every ten people you will ever meet live north of the equator.',
    },
    source: {
      citation: 'Derived from national population data',
      kind: 'estimated',
      assumptions: ['Estimates cluster between 87% and 90% depending on how split countries are allocated.'],
    },
  },
  {
    id: 'humans.wild-mammal-biomass',
    type: 'percentage',
    prompt: 'Of the combined mass of all mammals on Earth, what share is wild mammals?',
    subPrompt: 'Humans, livestock and wild mammals together make up the total.',
    category: 'Animals',
    difficulty: 3,
    tags: ['biomass', 'environment'],
    answer: 4,
    displayAnswer: 'about 4%',
    tolerance: 7,
    reveal: {
      headline: 'about 4%',
      explanation:
        'Livestock account for roughly 60% of mammal biomass, humans about 36%, and every wild mammal on Earth — every elephant, whale, mouse and bat — makes up the remaining few percent.',
      comparison: 'The world cattle herd outweighs all wild mammals by more than ten to one.',
    },
    source: {
      citation: 'Bar-On, Phillips & Milo, "The biomass distribution on Earth"',
      publisher: 'PNAS',
      year: 2018,
      kind: 'estimated',
    },
  },
  {
    id: 'humans.urban-share',
    type: 'percentage',
    prompt: 'What share of the world’s population lives in urban areas?',
    category: 'People',
    difficulty: 2,
    tags: ['cities'],
    answer: 57,
    displayAnswer: 'about 57%',
    tolerance: 11,
    reveal: {
      headline: 'about 57%',
      explanation:
        'The world crossed the halfway mark around 2007, the first time in history that most people lived in towns and cities. The share is still climbing by roughly half a percentage point a year.',
      comparison: 'In 1950 it was under 30%. Within your lifetime it will likely pass two thirds.',
    },
    source: {
      citation: 'World Urbanization Prospects',
      publisher: 'United Nations',
      statisticYear: 2023,
      url: 'https://ourworldindata.org',
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'humans.literacy',
    type: 'percentage',
    prompt: 'What percentage of the world’s adult population can read and write?',
    category: 'People',
    difficulty: 2,
    tags: ['education'],
    answer: 87,
    displayAnswer: 'about 87%',
    tolerance: 10,
    reveal: {
      headline: 'about 87%',
      explanation:
        'Global adult literacy sits around 87%, up from roughly 12% two centuries ago. The remaining gap is concentrated among older adults and in a small number of regions.',
      comparison: 'In 1820 barely one person in ten could read. It is one of the fastest social changes in human history.',
    },
    source: {
      citation: 'UNESCO Institute for Statistics',
      publisher: 'UNESCO',
      statisticYear: 2022,
      url: 'https://ourworldindata.org',
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'humans.births-per-day',
    type: 'numeric',
    prompt: 'How many babies are born worldwide each day?',
    category: 'People',
    difficulty: 2,
    tags: ['population'],
    answer: 385_000,
    displayAnswer: 'about 385,000',
    unit: 'births',
    magnitude: 'thousands',
    reveal: {
      headline: 'about 385,000 a day',
      explanation:
        'That is roughly 140 million births a year, against about 62 million deaths — which is why the world’s population still grows by around 70 million people annually.',
      comparison: 'Four and a half babies are born every second you spend reading this.',
    },
    source: {
      citation: 'World Population Prospects',
      publisher: 'United Nations',
      statisticYear: 2023,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'humans.centenarians',
    type: 'numeric',
    prompt: 'How many people alive today are aged 100 or older?',
    category: 'People',
    difficulty: 3,
    tags: ['ageing'],
    answer: 600_000,
    displayAnswer: 'about 600,000',
    unit: 'people',
    magnitude: 'thousands',
    tolerance: 0.36,
    reveal: {
      headline: 'about 600,000',
      explanation:
        'Roughly 600,000 centenarians are alive worldwide, and the number is rising fast — it has more than doubled since 2000 and is projected to pass three million by 2050.',
      comparison: 'Fewer than one person in 13,000 makes it to 100.',
    },
    source: {
      citation: 'World Population Prospects',
      publisher: 'United Nations',
      statisticYear: 2021,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'humans.life-expectancy',
    type: 'numeric',
    prompt: 'What is average life expectancy at birth, worldwide?',
    category: 'People',
    difficulty: 1,
    tags: ['health'],
    answer: 73,
    displayAnswer: 'about 73 years',
    unit: 'years',
    magnitude: 'small',
    tolerance: 0.12,
    reveal: {
      headline: 'about 73 years',
      explanation:
        'Global life expectancy dipped during the pandemic and has since recovered to roughly 73. In 1900 it was around 32, held down mostly by child mortality rather than by short adult lives.',
      comparison: 'The global average today is higher than the figure for any country on Earth in 1950.',
    },
    source: {
      citation: 'World Population Prospects',
      publisher: 'United Nations',
      statisticYear: 2023,
      url: 'https://ourworldindata.org',
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'humans.no-electricity',
    type: 'numeric',
    prompt: 'How many people in the world have no access to electricity?',
    category: 'People',
    difficulty: 2,
    tags: ['energy', 'development'],
    answer: 750_000_000,
    displayAnswer: 'about 750 million',
    unit: 'people',
    magnitude: 'millions',
    tolerance: 0.3,
    reveal: {
      headline: 'about 750 million',
      explanation:
        'Around 750 million people still live without electricity, roughly 80% of them in sub-Saharan Africa. The figure has fallen from about 1.7 billion in 2000.',
      comparison: 'Close to one person in ten has never flipped a light switch at home.',
    },
    source: {
      citation: 'World Energy Outlook / Tracking SDG7',
      publisher: 'International Energy Agency',
      statisticYear: 2022,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'humans.languages',
    type: 'numeric',
    prompt: 'How many languages are spoken in the world today?',
    category: 'People',
    difficulty: 2,
    tags: ['culture'],
    answer: 7100,
    displayAnswer: 'about 7,100',
    unit: 'languages',
    magnitude: 'thousands',
    tolerance: 0.28,
    reveal: {
      headline: 'about 7,100',
      explanation:
        'Roughly 7,100 living languages are catalogued, but they are wildly unevenly used: about half the world speaks one of just 23 of them, and around 40% of languages are endangered.',
      comparison: 'Papua New Guinea alone has more than 800 languages — over a tenth of the world’s total.',
    },
    source: {
      citation: 'Ethnologue: Languages of the World',
      publisher: 'SIL International',
      statisticYear: 2023,
      kind: 'measured',
      assumptions: ['The line between a language and a dialect is a judgement call, so counts vary.'],
      volatile: true,
    },
  },
  {
    id: 'humans.hair-count',
    type: 'numeric',
    prompt: 'About how many hairs are on a typical adult human head?',
    category: 'Everyday',
    difficulty: 2,
    tags: ['body'],
    answer: 100_000,
    displayAnswer: 'about 100,000',
    unit: 'hairs',
    magnitude: 'thousands',
    tolerance: 0.3,
    reveal: {
      headline: 'about 100,000',
      explanation:
        'Scalps carry roughly 100,000 follicles, varying with hair colour — natural blondes tend to have the most and redheads the fewest, though red hairs are individually thicker.',
      comparison: 'You shed around 100 of them a day, which is a full head of hair every three years.',
    },
    source: {
      citation: 'Standard dermatology reference figures',
      kind: 'estimated',
    },
  },
  {
    id: 'humans.rome-population',
    type: 'numeric',
    prompt: 'At its height around 150 CE, roughly how many people lived in the city of Rome?',
    category: 'History',
    difficulty: 3,
    tags: ['ancient', 'cities'],
    answer: 1_000_000,
    displayAnswer: 'about 1 million',
    unit: 'people',
    magnitude: 'millions',
    tolerance: 0.3,
    reveal: {
      headline: 'about 1 million',
      explanation:
        'Imperial Rome is generally reckoned to have held around a million people — fed by grain fleets from Egypt and supplied with water by eleven aqueducts.',
      comparison: 'No European city would be that large again until London in the early 1800s, more than 1,600 years later.',
    },
    source: {
      citation: 'Consensus range in Roman demographic scholarship',
      kind: 'estimated',
      assumptions: ['Scholarly estimates run from about 750,000 to 1.5 million.'],
    },
  },
  {
    id: 'humans.ww2-deaths',
    type: 'numeric',
    prompt: 'Approximately how many people died as a result of the Second World War?',
    subPrompt: 'Military and civilian deaths combined.',
    category: 'History',
    difficulty: 3,
    tags: ['war', 'modern'],
    answer: 75_000_000,
    displayAnswer: 'about 75 million',
    unit: 'people',
    magnitude: 'millions',
    tolerance: 0.28,
    reveal: {
      headline: 'about 75 million',
      explanation:
        'Estimates range from roughly 70 to 85 million, around two thirds of them civilians. The Soviet Union and China together account for more than half the total.',
      comparison: 'Around 3% of the entire world population in 1940.',
    },
    source: {
      citation: 'Standard historical estimates',
      kind: 'estimated',
      assumptions: [
        'Figures vary widely by source, especially for famine and disease deaths caused by the war.',
      ],
    },
  },
];
